import { Spacer } from '@spritz-finance/ui'
import React from 'react'
import { Location, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { FadePresence } from '../../components/FadePresence'
import { CenteredPageInner, PageContainer } from '../../components/Layout/PageContainer'
import { PageLoading } from '../../components/PageLoading'
import { PageTitle } from '../../components/PageTitle'
import { TabBar } from '../../components/TabBar/TabBar'
import { Feature, useFeatureFlag } from '../../context/useFeatureFlag'

import { PaymentProvider } from '../../graphql/generated/graphql'
import { NoTransferHistory } from '../bills/components/paymentsInProgress/NoTransferHistory'
import { NoTransfersInProgress } from '../bills/components/paymentsInProgress/NoTransfersInProgress'
import { PaymentHistory } from '../bills/components/paymentsInProgress/PaymentHistory'
import { PaymentsInProgress } from '../bills/components/paymentsInProgress/PaymentsInProgress'

import { TestPaymentInfoDialogProvider } from '../bills/useTestPaymentInfoDialog'

import { BankAccounts } from './components/BankAccounts'
import { EmbeddedVerificationModule } from '../verification/EmbeddedVerificationModule'
import { useVerificationContext, VerificationStatus } from '../verification/VerificationContext'

export interface IRoute {
    text: string
    path: string
    mobileText?: string
}

const routes: IRoute[] = [
    {
        text: 'All accounts',
        path: '',
    },
    {
        text: 'In progress',
        mobileText: 'In progress',
        path: 'in-progress',
    },
    {
        text: 'Completed',
        path: 'complete',
    },
]

const OffRampSubRoutes: React.FC<{
    location: Location
}> = ({ location }) => {
    return (
        <Routes location={location}>
            <Route path="" element={<BankAccounts />} />
            <Route
                path="in-progress"
                element={
                    <PaymentsInProgress
                        provider={PaymentProvider.Checkbook}
                        EmptyState={NoTransfersInProgress}
                        paymentType="offramp"
                    />
                }
            />
            <Route
                path="complete"
                element={
                    <PaymentHistory
                        provider={PaymentProvider.Checkbook}
                        EmptyState={NoTransferHistory}
                        paymentType="offramp"
                    />
                }
            />
            <Route path="*" element={<Navigate to="" />} />
        </Routes>
    )
}

export const OffRampPage = () => {
    const location = useLocation()

    const { verificationStatus, loading } = useVerificationContext()

    const offrampEnabled = useFeatureFlag(Feature.OffRamp)
    if (!offrampEnabled) return null

    const usageAllowed = verificationStatus == VerificationStatus.Verified

    const subRoute = location.pathname.split('/')[2] ?? ''

    if (loading) return <PageLoading />

    return (
        <PageContainer>
            <PageTitle subtitle="Add bank accounts, transfer crypto, and track your ACH transfers below.">
                Off-ramp
            </PageTitle>
            {usageAllowed && <TabBar routes={routes} subRoute={subRoute} />}
            <Spacer height={32} />
            <CenteredPageInner $width={698}>
                <FadePresence renderKey={location.pathname}>
                    {usageAllowed && <OffRampSubRoutes location={location} />}
                    {!usageAllowed && <EmbeddedVerificationModule />}
                </FadePresence>
            </CenteredPageInner>
        </PageContainer>
    )
}
